import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, Monitor, RefreshCw, Server, X } from 'lucide-react';
import { DESKTOP_DOWNLOAD_URL, desktop } from '@/lib/desktop';
import { readStored, writeStored } from '@/lib/browse';
import {
  alternativeLabel,
  alternatives,
  downloadLabel,
  formatOf,
  isPhoneOrTablet,
  recommended,
  shortLabel,
  useDetectedPlatform,
  useLatestDesktopRelease,
} from '@/lib/desktopDownload';
import { formatBytes } from '@/lib/format';
import { Badge, Button, Card } from './ui';
import { useDesktopUpdate } from './DesktopUpdateBanner';

const UPDATE_TEXT: Record<string, string> = {
  disabled: 'Automatic updates are off in this build',
  idle: 'Checks for updates automatically',
  checking: 'Checking for updates…',
  'not-available': 'Up to date',
  error: 'Could not check for updates',
};

/** A link to the download page: in-app navigation, or a new tab for an external VITE_DESKTOP_DOWNLOAD_URL. */
export function DesktopDownloadLink({ className, children }: { className?: string; children: ReactNode }) {
  return DESKTOP_DOWNLOAD_URL.startsWith('/') ? (
    <Link to={DESKTOP_DOWNLOAD_URL} className={className}>
      {children}
    </Link>
  ) : (
    <a href={DESKTOP_DOWNLOAD_URL} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

/**
 * Account page card: in the browser, a download button for this computer
 * and a link to the other platforms; inside the desktop app, its version,
 * update status and server.
 */
export function DesktopAppCard() {
  if (!desktop) return <DesktopDownloadCard />;
  return <DesktopInfoCard />;
}

function DesktopDownloadCard() {
  const latest = useLatestDesktopRelease();
  const detected = useDetectedPlatform();
  const best = latest.status === 'ok' ? recommended(latest.release, detected) : null;
  return (
    <Card className="mt-6 max-w-xl p-5">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent-2">
          <Monitor size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Desktop app</div>
          <p className="mt-1 text-sm text-muted">
            vidmakr for macOS, Windows and Linux. Import straight from camera cards and external drives, get native notifications, and
            update automatically.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {best ? (
              <a href={best.url}>
                <Button variant="primary">
                  <Download size={16} /> Download for {downloadLabel(best)}
                </Button>
              </a>
            ) : (
              <DesktopDownloadLink>
                <Button variant="primary">
                  <Download size={16} /> Download the desktop app
                </Button>
              </DesktopDownloadLink>
            )}
            {best ? (
              <DesktopDownloadLink className="text-sm text-muted hover:text-fg">Other platforms</DesktopDownloadLink>
            ) : null}
          </div>
          {latest.status === 'ok' && best ? <p className="mt-2 text-xs text-faint">Version {latest.release.version}</p> : null}
        </div>
      </div>
    </Card>
  );
}

function DesktopInfoCard() {
  const status = useDesktopUpdate();
  const [server, setServer] = useState('');
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    void desktop!.serverUrl().then(setServer);
  }, []);
  const state = status?.state ?? 'idle';
  const text =
    state === 'downloading'
      ? `Downloading ${status?.version ?? 'update'}… ${status?.percent ?? 0}%`
      : state === 'downloaded'
        ? `${status?.version} is ready to install`
        : state === 'error'
          ? `${UPDATE_TEXT.error}: ${status?.error ?? ''}`
          : UPDATE_TEXT[state];
  return (
    <Card className="mt-6 max-w-xl p-5">
      <div className="mb-4 flex items-center gap-3">
        <Monitor size={18} className="text-accent-2" />
        <div className="font-semibold">Desktop app</div>
        <Badge>v{desktop!.version}</Badge>
      </div>
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <RefreshCw size={14} className="text-muted" />
          <span className="min-w-0 flex-1 text-muted">{text}</span>
          {state === 'downloaded' ? (
            <Button variant="primary" size="sm" onClick={() => void desktop!.installUpdate()}>
              Restart to update
            </Button>
          ) : (
            <Button
              size="sm"
              loading={checking}
              disabled={state === 'checking' || state === 'downloading'}
              onClick={async () => {
                setChecking(true);
                await desktop!.checkForUpdates().finally(() => setChecking(false));
              }}
            >
              Check for updates
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Server size={14} className="text-muted" />
          <span className="min-w-0 flex-1 truncate text-muted">Connected to {server || '…'}</span>
          <Button size="sm" onClick={() => void desktop!.changeServer()}>
            Change server
          </Button>
        </div>
        <Link to="/download" className="inline-flex items-center gap-1 text-xs text-faint hover:text-fg">
          Release notes <ExternalLink size={11} />
        </Link>
      </div>
    </Card>
  );
}

/**
 * Login page addition inside the desktop app: sign in through the system
 * browser (needed for email links, which open there; Google sign-in is sent
 * there automatically).
 */
export function DesktopSignIn({ redirect }: { redirect: string }) {
  const [started, setStarted] = useState(false);
  if (!desktop) return null;
  return (
    <div className="mt-4 rounded-xl border border-line bg-panel-2 p-4 text-sm">
      <p className="text-muted">
        {started
          ? 'Finish signing in in your browser. It sends you back here automatically.'
          : 'Email sign-in links open in your browser. Sign in there and vidmakr opens again, signed in.'}
      </p>
      <Button className="mt-3 w-full" onClick={() => void desktop!.signInWithBrowser(redirect).then(() => setStarted(true))}>
        <ExternalLink size={14} /> {started ? 'Open the browser again' : 'Sign in with your browser'}
      </Button>
    </div>
  );
}

/**
 * User menu block: one click downloads the installer for this computer, with
 * the other builds for the same system and "Other platforms" underneath.
 * Hidden inside the desktop app; a plain link while no release is available,
 * and on phones and tablets.
 */
export function DesktopMenuDownload() {
  const latest = useLatestDesktopRelease();
  const detected = useDetectedPlatform();
  if (desktop || latest.status === 'loading' || !detected) return null;
  const release = latest.status === 'ok' ? latest.release : null;
  const best = release ? recommended(release, detected) : null;
  if (!release || !best) {
    return (
      <DesktopDownloadLink className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted hover:bg-panel-3 hover:text-fg">
        <Monitor size={14} />
        <span className="min-w-0 flex-1">
          {release && isPhoneOrTablet(detected) ? 'Desktop app available for Mac, Windows and Linux' : 'Desktop app'}
        </span>
      </DesktopDownloadLink>
    );
  }
  const others = alternatives(release, best);
  return (
    <div className="min-w-[250px] px-2 py-1">
      <a
        href={best.url}
        className="flex items-center gap-2.5 rounded-lg border border-line-2 bg-panel px-2.5 py-2 text-sm font-medium transition-colors hover:border-accent/60 hover:bg-accent/10"
      >
        <Download size={15} className="shrink-0 text-accent-2" />
        <span className="min-w-0 flex-1 truncate">Download for {shortLabel(best)}</span>
        <span className="shrink-0 text-xs font-normal text-faint">{formatBytes(best.sizeBytes)}</span>
      </a>
      <div className="mt-1 flex flex-wrap items-center gap-x-1.5 px-1 text-[11px] text-faint">
        <span>{formatOf(best)}</span>
        {others.map((o) => (
          <span key={o.variant}>
            ·{' '}
            <a href={o.url} className="hover:text-fg hover:underline">
              {alternativeLabel(o)}
            </a>
          </span>
        ))}
        <span>
          ·{' '}
          <DesktopDownloadLink className="hover:text-fg hover:underline">Other platforms</DesktopDownloadLink>
        </span>
      </div>
    </div>
  );
}

const PROMO_KEY = 'vm.desktopPromoDismissed';

/**
 * A quiet sidebar card pointing to the desktop app, until it is dismissed
 * (remembered in this browser). Only while a release is available.
 */
export function DesktopSidebarPromo() {
  const [dismissed, setDismissed] = useState(() => readStored(PROMO_KEY, false));
  const latest = useLatestDesktopRelease();
  const detected = useDetectedPlatform();
  if (desktop || dismissed || latest.status !== 'ok' || !detected) return null;
  const best = recommended(latest.release, detected);
  const phone = isPhoneOrTablet(detected);
  return (
    <div className="group relative rounded-xl border border-line bg-panel-2 transition-colors hover:border-line-2">
      <DesktopDownloadLink className="flex items-center gap-2.5 py-2.5 pr-7 pl-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent-2">
          <Monitor size={14} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-medium">Get the desktop app</span>
          <span className="block truncate text-[11px] text-faint">{phone || !best ? 'For Mac, Windows and Linux' : `For ${shortLabel(best)}`}</span>
        </span>
      </DesktopDownloadLink>
      <button
        aria-label="Hide"
        title="Hide"
        className="absolute top-1.5 right-1.5 rounded-md p-1 text-faint hover:bg-panel-3 hover:text-fg"
        onClick={() => {
          writeStored(PROMO_KEY, true);
          setDismissed(true);
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
}
